import type { ContactInsights } from '../types/contactInsights';
import { averageGapDays, daysBetween, latestDate } from './orderCadence';

// Within this many days of the usual gap, a contact is "due soon"
const DUE_SOON_DAYS = 14;

export type CadenceState = {
  gapDays: number;
  status: 'on-track' | 'due-soon' | 'lapsed';
  /** Days past the usual gap (lapsed) or until it (due soon) */
  days: number;
};

// Same rule as the contact page's Ordering Cadence card
export function getCadence(insights: ContactInsights): CadenceState | null {
  const gapDays = averageGapDays(insights.watchedOrderDates);
  const last = latestDate(insights.watchedOrderDates);
  if (gapDays === null || !last) return null;
  const daysSince = daysBetween(last);
  const overdue = daysSince - gapDays;
  if (overdue > 0) return {
    gapDays,
    status: 'lapsed',
    days: overdue
  };
  if (-overdue <= DUE_SOON_DAYS) return {
    gapDays,
    status: 'due-soon',
    days: -overdue
  };
  return {
    gapDays,
    status: 'on-track',
    days: -overdue
  };
}

// "6 wks", "10 days", "4 mths"
export function formatShortSpan(days: number): string {
  if (days < 14) return `${days} ${days === 1 ? 'day' : 'days'}`;
  if (days < 120) return `${Math.round(days / 7)} wks`;
  return `${Math.round(days / 30.44)} mths`;
}

export function formatDayMonth(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short'
  });
}

export function initialsOf(name: string): string {
  return name.split(' ').filter(Boolean).map(part => part[0]).join('').toUpperCase();
}

// Sort values for the insight columns on the Contacts table
export function insightSortValue(insights: ContactInsights, key: string): number | string | null {
  switch (key) {
    case 'callFlag':
      return insights.callFlag === 'none' ? 'zz' : insights.callFlag;
    case 'cadence':
      {
        const cadence = getCadence(insights);
        if (!cadence) return -Infinity;
        // Most overdue first when sorted descending
        return cadence.status === 'lapsed' ? cadence.days : -cadence.days;
      }
    case 'lastContacted':
      return insights.lastContacted ? insights.lastContacted.date : '';
    case 'trend':
      return insights.spendTrendPct;
    case 'skipsPauses':
      return insights.skips + insights.pauses;
    case 'clubOneTime':
      return insights.clubOrders + insights.oneTimeOrders;
    case 'openTasks':
      return insights.openTaskAssignees.length;
    case 'topProduct':
      return insights.topProduct;
    default:
      return null;
  }
}
